import { Box, Button, Flex, Heading, HStack, Icon, Image, keyframes, Spacer, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiList, FiLock, FiEye, FiCalendar, FiCheckCircle, FiClock, FiChevronRight, FiMail, FiCheckSquare, FiAward } from "react-icons/fi";
import { getLandingPage, convertHtmlToText } from "@lubbezposrednio-frontend/core";
import { AdaptiveShell } from "../../OrganizerDashboardView/components/AdaptiveShell";
import { VotingPreviewBox } from "../../LandingPageView/components/VotingPreviewBox";
import { SignUpForVotingForm } from "../../LandingPageView/components/SignUpForVotingForm";
import { LandingPageQR } from "../../LandingPageView/components/LandingPageQR";
import { LandingPageDescriptions } from "../../LandingPageView/components/LandingPageDescriptions";
import { LandingPageViewModel } from "../../LandingPageView/LandingPageViewModel";
import { VotingViewModel } from "../VotingViewModel";
import { Countdown } from "./Countdown";
import { PhaseTimeline, Phase } from "./PhaseTimeline";
import { ShareInvite } from "./ShareInvite";
import { AddToCalendarButton } from "./AddToCalendarButton";

const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -22px) scale(1.06); }
`;

const HeroChip: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <HStack position="relative" spacing="6px" bg="rgba(255,255,255,0.14)" border="1px solid rgba(255,255,255,0.2)" borderRadius="full" px="12px" py="6px">
    <Icon as={icon} boxSize="13px" />
    <Text fontSize="13px" fontWeight="600">
      {children}
    </Text>
  </HStack>
);

const plQuestions = (n: number) =>
  n === 1 ? "pytanie" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "pytania" : "pytań";

const plPeople = (n: number) =>
  n === 1 ? "osoba" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "osoby" : "osób";

// Sekcja „Jak to działa" — przeniesiona ze strony zapisu (LandingPageView), pokazywana dla głosowań typu „signup".
const STEPS = [
  { icon: FiMail, text: "Zapisujesz się e-mailem (lub jednym kliknięciem, gdy jesteś zalogowany)." },
  { icon: FiCheckSquare, text: "Potwierdzasz adres linkiem z wiadomości." },
  { icon: FiList, text: "Głosujesz anonimowo, gdy głosowanie ruszy." },
  { icon: FiAward, text: "Wynik zostaje zakotwiczony na blockchainie — publicznie weryfikowalny." }
];

const StatusCard: React.FC<{ icon: React.ElementType; tone: string; iconColor: string; title: string; children: React.ReactNode }> = ({ icon, tone, iconColor, title, children }) => (
  <Box bg="white" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.12)" borderRadius="22px" p={{ base: "24px", md: "28px" }}>
    <Flex w="52px" h="52px" borderRadius="15px" bg={tone} align="center" justify="center" mb="16px">
      <Icon as={icon} boxSize="26px" color={iconColor} />
    </Flex>
    <Heading fontSize="21px" fontWeight="800" color="primary" mb="8px">
      {title}
    </Heading>
    {children}
  </Box>
);

export const VotingPlannedView: React.FC<{ model: VotingViewModel }> = observer(({ model }) => {
  const navigate = useNavigate();
  const qCount = model.questionsPreview.length;
  const isSignup = model.electorateType === "signup" && !!model.signupLandingId;
  const needsSignupForm = isSignup && !model.signedUp;

  const phases: Phase[] = [
    { label: isSignup ? "Zapisy" : "Zaplanowane", date: isSignup ? "otwarte" : undefined, state: "current" },
    { label: "Głosowanie", date: model.startDate, state: "upcoming" },
    { label: "Wyniki", date: model.endDate, state: "upcoming" }
  ];

  const [landingModel] = useState(() => new LandingPageViewModel());
  const [landingReady, setLandingReady] = useState(false);

  // Ładuj dane strony zapisu dla całego głosowania typu „signup" (także po zapisaniu się) —
  // żeby pokazać opis, „jak to działa", QR i dodatkowe sekcje przeniesione z LandingPageView.
  useEffect(() => {
    if (!isSignup || !model.signupLandingId) return;
    let active = true;
    getLandingPage(model.signupLandingId)
      .then(res => {
        if (!active || !res.data?.landing_page) return;
        landingModel.getInitialData(res.data.landing_page);
        landingModel.onInit();
        setLandingReady(true);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isSignup, model.signupLandingId]);

  const pageDescription = landingReady ? convertHtmlToText(landingModel.textContents?.pageDescription ?? "") : "";

  const action = model.signedUp ? (
    <StatusCard icon={FiCheckCircle} tone="rgba(47,206,37,0.14)" iconColor="#1f9e18" title="Jesteś zapisany">
      <Text fontSize="14px" color="gray.500" lineHeight="1.6">
        Wszystko gotowe — głosowanie ruszy <strong>{model.startDate}</strong>. Damy Ci znać, gdy będzie można oddać głos.
      </Text>
      {model.startDateRaw && (
        <Box mt="16px">
          <AddToCalendarButton title={model.name ?? ""} start={model.startDateRaw} end={model.endDateRaw} url={window.location.href} />
        </Box>
      )}
      {model.isPublic && isSignup && <ShareInvite url={window.location.href} />}
    </StatusCard>
  ) : needsSignupForm ? (
    landingReady ? (
      <SignUpForVotingForm model={landingModel} />
    ) : (
      <Flex bg="white" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 16px 40px rgba(67,0,152,0.12)" borderRadius="22px" p="40px" justify="center">
        <Spinner color="primary" size="lg" thickness="3px" />
      </Flex>
    )
  ) : (
    <StatusCard icon={FiClock} tone="rgba(67,0,152,0.08)" iconColor="primary" title="Głosowanie zaplanowane">
      <Text fontSize="14px" color="gray.500" lineHeight="1.6">
        Głosowanie ruszy <strong>{model.startDate}</strong>. Wróć tutaj, gdy się rozpocznie.
      </Text>
    </StatusCard>
  );

  return (
    <AdaptiveShell>
      <Box w="100%" maxW="620px" mx="auto" px={{ base: "16px", md: "20px" }} pt={{ base: "14px", md: "20px" }}>
        <Button onClick={() => navigate(-1)} variant="ghost" size="sm" color="gray.500" leftIcon={<Icon as={FiArrowLeft} />} fontWeight="700" _hover={{ color: "primary" }}>
          Wstecz
        </Button>
      </Box>
      <Box w="100%" maxW="620px" mx="auto" px={{ base: "16px", md: "20px" }} pt={{ base: "8px", md: "12px" }} pb={{ base: "28px", md: "44px" }}>
        <VStack align="stretch" spacing="18px">
          {/* HERO */}
          <Box position="relative" overflow="hidden" borderRadius="28px" color="white" p={{ base: "30px 24px", md: "40px 38px" }} bgGradient="linear(150deg, #25004f 0%, #430098 46%, #6a1fc2 100%)" boxShadow="0 24px 60px rgba(40,0,90,0.32)">
            <Box position="absolute" w="320px" h="320px" bg="#8b2fd6" opacity={0.4} top="-110px" right="-70px" borderRadius="full" filter="blur(70px)" animation={`${float} 12s ease-in-out infinite`} pointerEvents="none" />
            <Box position="absolute" w="240px" h="240px" bg="#E51F44" opacity={0.18} bottom="-90px" left="-50px" borderRadius="full" filter="blur(70px)" animation={`${float} 12s ease-in-out infinite`} sx={{ animationDelay: "3s" }} pointerEvents="none" />

            <Text position="relative" fontSize="12.5px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" opacity={0.8} mb="12px">
              Głosowanie zaplanowane
            </Text>
            <Heading position="relative" fontSize={{ base: "27px", md: "34px" }} fontWeight="800" lineHeight="1.12" letterSpacing="-0.5px">
              {model.name}
            </Heading>
            {isSignup && pageDescription && (
              <Text position="relative" fontSize={{ base: "15px", md: "16px" }} opacity={0.88} lineHeight="1.6" mt="14px">
                {pageDescription}
              </Text>
            )}

            <HStack position="relative" spacing="8px" mt="18px" wrap="wrap">
              {qCount > 0 && (
                <HeroChip icon={FiList}>
                  {qCount} {plQuestions(qCount)}
                </HeroChip>
              )}
              {typeof model.secret === "boolean" && (
                <HeroChip icon={model.secret ? FiLock : FiEye}>{model.secret ? "Głosowanie tajne" : "Jawne imienne"}</HeroChip>
              )}
              {model.startDate && <HeroChip icon={FiCalendar}>Start {model.startDate}</HeroChip>}
            </HStack>

            {model.signupsCount > 0 && (
              <HStack position="relative" spacing="11px" mt="18px">
                <HStack spacing="-9px">
                  {["#a45cff", "#E51F44", "#8b2fd6"].map((c, i) => (
                    <Box key={i} w="30px" h="30px" borderRadius="full" bg={c} border="2.5px solid #2f0a63" />
                  ))}
                </HStack>
                <Text fontSize="14px" fontWeight="600" opacity={0.92}>
                  Już <strong>{model.signupsCount}</strong> {plPeople(model.signupsCount)} {isSignup ? "zapisanych" : "uprawnionych"}
                </Text>
              </HStack>
            )}

            {model.startDateRaw && (
              <Box position="relative" mt="24px">
                <Text fontSize="11.5px" fontWeight="600" opacity={0.7} mb="10px" textTransform="uppercase" letterSpacing="0.06em">
                  Głosowanie zaczyna się za
                </Text>
                <Countdown target={model.startDateRaw} />
              </Box>
            )}
          </Box>

          {/* AKCJA — od razu pod hero */}
          {action}

          {/* Oś czasu */}
          <PhaseTimeline phases={phases} />

          {/* Pytania */}
          {qCount > 0 && <VotingPreviewBox preview={{ title: model.name ?? "", secret: !!model.secret, questions: model.questionsPreview }} />}

          {/* Organizator */}
          {model.organizer && (
            <Box
              bg="white"
              borderRadius="20px"
              boxShadow="0 8px 24px rgba(67,0,152,0.06)"
              p={{ base: "18px", md: "22px" }}
              role="group"
              {...(model.organizer.profileRef
                ? {
                    as: RouterLink,
                    to: `/profil/${model.organizer.profileRef}`,
                    display: "block",
                    transition: "all 0.15s",
                    _hover: { boxShadow: "0 14px 32px rgba(67,0,152,0.13)", transform: "translateY(-2px)" }
                  }
                : {})}
            >
              <Text fontSize="11.5px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="gray.400" mb="12px">
                Organizuje
              </Text>
              <HStack spacing="13px">
                {model.organizer.imageUrl ? (
                  <Image src={model.organizer.imageUrl} alt={model.organizer.name} boxSize="48px" borderRadius="12px" objectFit="cover" />
                ) : (
                  <Flex boxSize="48px" borderRadius="12px" bg="rgba(67,0,152,0.08)" align="center" justify="center" color="primary" fontWeight="800" fontSize="18px">
                    {(model.organizer.name ?? "?").charAt(0).toUpperCase()}
                  </Flex>
                )}
                <Text fontSize="17px" fontWeight="700" color="primary">
                  {model.organizer.name}
                </Text>
                <Spacer />
                {model.organizer.profileRef && (
                  <Icon as={FiChevronRight} color="primary" boxSize="18px" _groupHover={{ transform: "translateX(2px)" }} transition="transform 0.12s" />
                )}
              </HStack>
            </Box>
          )}

          {/* Sekcje przeniesione ze strony zapisu (LandingPageView) — tylko dla głosowań typu „signup" */}
          {isSignup && landingReady && (
            <>
              {/* Jak to działa */}
              <Box bg="white" borderRadius="20px" boxShadow="0 8px 24px rgba(67,0,152,0.06)" p={{ base: "22px", md: "26px" }}>
                <Text fontSize="13px" fontWeight="700" color="primary" mb="16px">
                  Jak to działa
                </Text>
                <VStack align="stretch" spacing="14px">
                  {STEPS.map((s, i) => (
                    <HStack key={i} align="flex-start" spacing="13px">
                      <Flex w="30px" h="30px" borderRadius="9px" bg="rgba(67,0,152,0.07)" align="center" justify="center" flexShrink={0}>
                        <Icon as={s.icon} boxSize="15px" color="primary" />
                      </Flex>
                      <Text fontSize="14px" color="gray.700" pt="5px" lineHeight="1.5">
                        {s.text}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <LandingPageQR model={landingModel} />
              <LandingPageDescriptions model={landingModel} />
            </>
          )}
        </VStack>
      </Box>
    </AdaptiveShell>
  );
});
